import { useRef } from "react";

import { toPng } from "html-to-image";
import { useAtom } from "jotai";
import {
  Download,
  FileSpreadsheetIcon,
  ImageIcon,
  Loader2,
  LucideSend,
} from "lucide-react";
import { toast } from "sonner";

import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { Button } from "src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { useAuth } from "src/lib/auth";
import {
  reportingIntegrationTypes,
  storeIntegrationTypes,
} from "src/lib/constants";
import { dateFormat } from "src/lib/format";
import { useApiMutation } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import {
  REPORT_TO_EXCEL_FILE_MUTATION,
  REPORT_TO_SLACK_MUTATION,
} from "src/lib/queries/reports";
import { cn } from "src/lib/utils";

function toggleExportElements(showing) {
  document.querySelectorAll("[data-export-hide]").forEach((el) => {
    if (showing) {
      el.classList.add("!hidden", "hidden");
    } else {
      el.classList.remove("!hidden", "hidden");
    }
  });

  document.querySelectorAll("[data-export-show]").forEach((el) => {
    if (showing) {
      el.classList.remove("!hidden", "hidden");
    } else {
      el.classList.add("!hidden", "hidden");
    }
  });
}

export function DataExportButton({
  imageExportRef,
  tableData,
  tableHeaders,
  title,
  integrationName,
  dateFrom,
  dateTo,
  className,
  asIcon,
  label = "Export",
  icon: ButtonIcon = Download,
  menuLabel,
  itemLabel = "report",
  slackLabel = "Report to Slack",
  contentClassName,
  subContentClassName,
  sendSeparately,
  disabled,
  styles = [],
}) {
  const { currentUser } = useAuth();
  const { selectedIntegration } = useIntegration(
    storeIntegrationTypes.shopify,
    { queryOnRender: false }
  );
  const exportScope = `${selectedIntegration?.id}:${selectedIntegration?.currency}`;
  const scopeRef = useRef({ key: exportScope });
  if (scopeRef.current.key !== exportScope)
    scopeRef.current = { key: exportScope };
  const currentScope = scopeRef.current;
  const { selectedIntegration: slackIntegration } = useIntegration(
    reportingIntegrationTypes.slack
  );
  const hasSlackEnabled = !!slackIntegration?.settings?.sources?.length;
  const { mutationApi: reportToSlack, loading: reportToSlackLoading } =
    useApiMutation({
      mutation: REPORT_TO_SLACK_MUTATION,
    });
  const { mutationApi: reportToExcelFile, loading: reportToExcelFileLoading } =
    useApiMutation({
      mutation: REPORT_TO_EXCEL_FILE_MUTATION,
    });

  const fileName = `${process.env.APP_NAME} ${title}${integrationName ? ` (${integrationName})` : ""}`;
  const integrationLabel = integrationName ? ` for ${integrationName}` : "";
  const sharerName = currentUser?.name || "A team member";

  const [, setSettingsStore] = useAtom(settingsDialogAtom);

  const generateImage = async () => {
    const exportNode = imageExportRef.current;

    if (!exportNode) {
      return null;
    }

    toggleExportElements(true);
    const previousExporting = exportNode.dataset.exporting;
    exportNode.dataset.exporting = "true";

    let imageUrl = null;
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      imageUrl = await toPng(exportNode, {
        cacheBust: true,
        pixelRatio: 4,
      });
    } finally {
      if (previousExporting === undefined) {
        delete exportNode.dataset.exporting;
      } else {
        exportNode.dataset.exporting = previousExporting;
      }
      toggleExportElements(false);
    }

    return scopeRef.current === currentScope ? imageUrl : null;
  };

  const openSlackSettings = () => {
    setSettingsStore((prev) => ({
      ...prev,
      open: true,
      activeTab: "account",
      manageParam: null,
      sectionTarget: "slack",
      sectionTargetNonce: Date.now(),
    }));
  };

  const downloadImage = async () => {
    if (disabled) return;
    const imageUrl = await generateImage();

    if (!imageUrl) return;
    const link = document.createElement("a");
    link.download = fileName || process.env.APP_NAME + "-chart.png";
    link.href = imageUrl;
    link.click();
  };

  const downloadExcel = () => {
    if (disabled) return;
    if (!tableData || !tableHeaders) {
      toast.error("No data available to export", {
        description:
          "Please ensure you have data in the table before exporting",
      });
      return;
    }

    toast.promise(
      reportToExcelFile({
        data: tableData,
        headers: tableHeaders,
        title,
        styles,
      }).then((response) => {
        if (response?.data?.reportToExcelFile) {
          const link = document.createElement("a");
          link.download = `${fileName || process.env.APP_NAME}.xlsx`;
          link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.reportToExcelFile}`;
          link.click();
        } else {
          throw new Error();
        }
      }),
      {
        loading: "Generating Excel file...",
        success: "Excel file generated successfully",
        error: "Failed to generate Excel file. Please try again",
      }
    );
  };

  const loading = disabled || reportToSlackLoading || reportToExcelFileLoading;

  return (
    <DropdownMenu modal className="z-50">
      <DropdownMenuTrigger asChild disabled={loading}>
        {asIcon ? (
          <Button
            variant="ghost"
            size="icon"
            className={className}
            aria-label={label}
            title={label}
            data-export-hide
          >
            {loading ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <ButtonIcon />
            )}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            aria-label={label}
            className={className}
            data-export-hide
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <ButtonIcon className="size-4" />
                {label}
              </>
            )}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn("w-44", contentClassName)}>
        <DropdownMenuLabel>{menuLabel || `${title} Export`}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {imageExportRef && (
            <DropdownMenuItem onSelect={downloadImage}>
              Download PNG
              <DropdownMenuShortcut>
                <ImageIcon />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}

          {tableData && (
            <DropdownMenuItem onSelect={downloadExcel}>
              Download Excel
              <DropdownMenuShortcut>
                <FileSpreadsheetIcon />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          )}

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>{slackLabel}</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className={subContentClassName}>
                {hasSlackEnabled ? (
                  slackIntegration.settings.sources.map(({ name, id }) => (
                    <DropdownMenuItem
                      key={name}
                      disabled={loading}
                      onSelect={async () =>
                        toast.promise(
                          reportToSlack({
                            channel: id,
                            title,
                            fileName,
                            image: imageExportRef && (await generateImage()),
                            data: tableData,
                            styles,
                            headers: tableHeaders,
                            sendSeparately: sendSeparately,
                            message: `${sharerName} has shared the following ${itemLabel}${integrationLabel}.\n\n${title}${dateFrom && dateTo ? ` ${dateFormat.default(dateFrom)} - ${dateFormat.default(dateTo)}` : ""}`,
                          }),
                          {
                            loading: `Sending ${title} ${itemLabel} to Slack...`,
                            success: `${title} ${itemLabel} sent to ${name} channel`,
                            error: `Failed to send ${itemLabel} to ${name} channel. Please try again`,
                          }
                        )
                      }
                    >
                      {name}
                      <DropdownMenuShortcut>
                        <LucideSend />
                      </DropdownMenuShortcut>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled>
                    No Slack channels connected
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={openSlackSettings}>
                  Slack settings...
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
